/**
 * AddEditPowerModal Component
 * Modal for adding a new power or editing an existing power in a report
 * Tailwind version using OathUI components
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  CalendarIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../../../services/oathAuthContext';
import usePermissions from '../../../../hooks/usePermissions';
import {
  getAvailablePowers,
  getExecutingAgents,
  getCities,
  addPowerToReport,
  updatePower,
  getAvailableRates,
  checkPowerActiveReport
} from '../../../../services/oathPowerReportService';
import { getPowerDetails } from '../../../../services/oathPowerInquiryService';
import {
  formatSSN,
  formatCurrencyWithSymbol,
  formatCurrencyInput,
  parseCurrencyInput,
  formatDateForInput,
  formatDateForDisplay,
  formatDateInputText,
  parseDateInput,
  toTitleCase
} from '../utils';
import { POWER_STATUS, VALIDATION_RULES, RATE_PRIORITY, RATE_TYPE } from '../utils/constants';
import { useAvailablePowersCache, useAgentsCitiesCache } from '../utils/hooks';

const INITIAL_FORM_DATA = {
  powerNumber: '',
  powerStatusCd: POWER_STATUS.UNEXECUTED,
  powerAmount: '',
  executionDate: null,
  caseNumber: '',
  offense: '',
  executingAgentId: null,
  executingAgent: null,
  cityId: null,
  city: null,
  executingState: '',
  defendantFirstName: '',
  defendantLastName: '',
  defendantDob: null,
  defendantSsn: '',
  scAmount: '',
  rateId: null  // Variable rate selection
};

const AddEditPowerModal = ({
  isOpen,
  onClose,
  reportId,
  power = null,
  onSave,
  onOpenProofDialog = null,
  preloadedAgents = [],
  preloadedCities = [],
  preloadedPowers = [],
  dataPreloaded = false,
  isSouthCarolina = false,
  agencyId: propAgencyId = null,
  existingPowers = []
}) => {
  const { user } = useOathAuth();
  const { canAccessContract, canAccessVariableRate } = usePermissions();
  const isGaUser = user?.agencies?.some(a => a.isGeneralAgent);

  // Caching hooks for performance
  const { getCachedPowers, setCachedPowers, invalidatePowersCache } = useAvailablePowersCache();
  const { getCachedAgents, setCachedAgents, getCachedCities, setCachedCities } = useAgentsCitiesCache();

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [loadingPowers, setLoadingPowers] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Dropdown data
  const [availablePowers, setAvailablePowers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [cities, setCities] = useState([]);

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Date text input states
  const [executionDateText, setExecutionDateText] = useState('');
  const [dobText, setDobText] = useState('');

  // Currency display states (formatted with commas)
  const [powerAmountDisplay, setPowerAmountDisplay] = useState('');
  const [scAmountDisplay, setScAmountDisplay] = useState('');

  // Refs for hidden date picker inputs and first field auto-focus
  const executionDatePickerRef = useRef(null);
  const dobDatePickerRef = useRef(null);
  const firstFieldRef = useRef(null);

  // Refs for dropdown containers (for click-outside detection)
  const powerDropdownRef = useRef(null);
  const agentDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Refs for dropdown list containers (for scroll-into-view)
  const powerListRef = useRef(null);
  const agentListRef = useRef(null);
  const cityListRef = useRef(null);

  // Flag to prevent dropdown from opening on initial auto-focus
  const isInitialFocusRef = useRef(true);

  // Synchronous guard against concurrent saves (rapid double/triple-click).
  // A ref updates immediately, unlike the `loading` state whose disabled
  // re-render hasn't flushed yet, so it blocks the duplicate submission.
  const saveInProgressRef = useRef(false);

  // Ref to always have the latest onOpenProofDialog callback (avoids stale closure in useCallback)
  const onOpenProofDialogRef = useRef(onOpenProofDialog);
  onOpenProofDialogRef.current = onOpenProofDialog;

  // Ref to always have the latest loadRates callback (avoids stale closure in debounced effect)
  const loadRatesRef = useRef(null);
  // AbortController ref to cancel in-flight rate requests when superseded or modal closes
  const rateAbortControllerRef = useRef(null);

  // Threshold confirmation dialog
  const [showThresholdDialog, setShowThresholdDialog] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null);
  const [selectedPowerDenomination, setSelectedPowerDenomination] = useState(0);
  const [thresholdAcknowledged, setThresholdAcknowledged] = useState(false);

  // Search states for autocomplete
  const [powerSearch, setPowerSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showPowerDropdown, setShowPowerDropdown] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // Keyboard navigation indices for dropdowns
  const [highlightedCityIndex, setHighlightedCityIndex] = useState(-1);
  const [highlightedAgentIndex, setHighlightedAgentIndex] = useState(-1);
  const [highlightedPowerIndex, setHighlightedPowerIndex] = useState(-1);

  // Rate and premium calculation states
  const [availableRates, setAvailableRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [proofAttributeValues, setProofAttributeValues] = useState([]);

  // Power status lookup (for typed power numbers not in available powers)
  const [powerStatusLookup, setPowerStatusLookup] = useState(null);

  // Derived state
  const isVoid = formData.powerStatusCd === POWER_STATUS.VOID;
  const isEditMode = !!power;

  // Create lookup maps
  const agentMap = useMemo(() => {
    const map = new Map();
    agents.forEach(agent => {
      map.set(agent.executingAgentId, agent);
      map.set(`${agent.firstName}_${agent.lastName}`, agent);
    });
    return map;
  }, [agents]);

  const cityMap = useMemo(() => {
    const map = new Map();
    cities.forEach(city => {
      map.set(city.cityId, city);
      map.set(`${city.name}_${city.stateCode}`, city);
    });
    return map;
  }, [cities]);

  // Filter available powers based on search
  const filteredPowers = useMemo(() => {
    // Ensure availablePowers is an array
    const powers = Array.isArray(availablePowers) ? availablePowers : [];
    if (!powerSearch) return powers;
    const search = powerSearch.toLowerCase();
    return powers.filter(p =>
      p.powerNumber?.toLowerCase().includes(search) ||
      (isGaUser && p.agencyName?.toLowerCase().includes(search))
    );
  }, [availablePowers, powerSearch, isGaUser]);

  // Helper — true when the agent is flagged as the agency's primary.
  const isPrimaryAgent = (a) => Boolean(a?.isPrimary);

  // Filter agents based on search, and float the primary agent to the top so
  // it's the first thing the user sees in the dropdown.
  const filteredAgents = useMemo(() => {
    const search = agentSearch.toLowerCase();
    const matching = !agentSearch
      ? agents
      : agents.filter(a => `${a.firstName} ${a.lastName}`.toLowerCase().includes(search));
    // Stable sort: primary agents first, everyone else in original order.
    return [...matching].sort((a, b) => {
      const aP = isPrimaryAgent(a) ? 0 : 1;
      const bP = isPrimaryAgent(b) ? 0 : 1;
      return aP - bP;
    });
  }, [agents, agentSearch]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return cities;
    const search = citySearch.toLowerCase();
    return cities.filter(c =>
      `${c.name}, ${c.stateCode}`.toLowerCase().startsWith(search)
    );
  }, [cities, citySearch]);

  // Filter rates by power amount range (client-side filtering per legacy logic)
  const filteredRates = useMemo(() => {
    if (!availableRates.length) return [];
    const amount = parseFloat(formData.powerAmount) || 0;

    let rates = availableRates;

    // Filter out variable rates if user lacks permission
    if (!canAccessVariableRate) {
      rates = rates.filter(r => r.priority !== RATE_PRIORITY.VARIABLE);
    }

    if (amount <= 0) return rates; // Show all if no amount yet

    return rates.filter(rate => {
      const minOk = !rate.minAmount || rate.minAmount <= amount;
      const maxOk = !rate.maxAmount || rate.maxAmount >= amount;
      return minOk && maxOk;
    });
  }, [availableRates, formData.powerAmount, canAccessVariableRate]);

  // Get the currently selected rate
  const selectedRate = useMemo(() => {
    if (!formData.rateId || filteredRates.length === 0) return null;
    return filteredRates.find(r => r.rateId === formData.rateId) || null;
  }, [formData.rateId, filteredRates]);

  // Format rate display text for dropdown
  const formatRateDisplay = useCallback((rate) => {
    if (!rate) return '';

    // Hide rate values when user lacks contract visibility
    if (!canAccessContract) {
      const name = rate.name || 'Rate';
      return { displayName: name, rateValue: '', isVariable: rate.priority === RATE_PRIORITY.VARIABLE, fullDisplay: name };
    }

    // Build rate value display
    let rateValue = '';
    if (rate.rateTypeCode === RATE_TYPE.PERCENTAGE) {
      rateValue = `${rate.rate1}%`;
    } else {
      rateValue = `$${rate.rate1?.toLocaleString() || '0'}`;
    }

    // Show name with rate value, or just rate value if no name
    const displayName = rate.name ? `${rate.name} (${rateValue})` : rateValue;

    return {
      displayName,
      rateValue,
      isVariable: rate.priority === RATE_PRIORITY.VARIABLE,
      fullDisplay: displayName
    };
  }, [canAccessContract]);

  // Get agency ID from prop (preferred) or Oath user
  const agencyId = propAgencyId || user?.agencies?.[0]?.agencyId;

  // GA users should fetch powers using their main agency ID to see the full hierarchy inventory
  const powersAgencyId = isGaUser
    ? (user?.agencies?.find(a => a.isMainAgency)?.agencyId || agencyId)
    : agencyId;

  // Check if a power number is already in the current report (prevents duplicates)
  const isPowerInCurrentReport = useCallback((powerNumber) => {
    if (!powerNumber || !existingPowers.length) return false;
    // When editing, exclude the power being edited from the duplicate check
    return existingPowers.some(p =>
      p.powerNumber === powerNumber &&
      (!power || p.powerNumber !== power.powerNumber)
    );
  }, [existingPowers, power]);

  // Load available powers (with caching)
  const loadAvailablePowers = useCallback(async (forceRefresh = false) => {
    if (!powersAgencyId) {
      toast.error('Agency information not available');
      return;
    }

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cachedPowers = getCachedPowers(powersAgencyId);
      if (cachedPowers) {
        setAvailablePowers(cachedPowers);
        return;
      }
    }

    try {
      setLoadingPowers(true);

      const result = await getAvailablePowers(powersAgencyId);

      if (result.success) {
        // Ensure we always set an array
        const powers = Array.isArray(result.data) ? result.data : [];
        setAvailablePowers(powers);
        // Cache the results
        setCachedPowers(powersAgencyId, powers);
      } else {
        toast.error('Failed to load available powers: ' + (result.error || 'Unknown error'));
        setAvailablePowers([]);
      }
    } catch (error) {
      toast.error('Failed to load available powers: ' + error.message);
      setAvailablePowers([]);
    } finally {
      setLoadingPowers(false);
    }
  }, [powersAgencyId, getCachedPowers, setCachedPowers]);

  // Load agents and cities independently (each resolves its own loading state)
  const loadAgentsAndCities = useCallback(() => {
    if (!agencyId) {
      toast.error('Agency information not available');
      return;
    }

    // Check cache first - only use cached data if it has items
    const cachedAgentsData = getCachedAgents(agencyId);
    const cachedCitiesData = getCachedCities(agencyId);

    if (cachedAgentsData?.length > 0 && cachedCitiesData?.length > 0) {
      setAgents(cachedAgentsData);
      setCities(cachedCitiesData);
      return;
    }

    // Load agents - resolves independently
    if (cachedAgentsData?.length > 0) {
      setAgents(cachedAgentsData);
    } else {
      setLoadingAgents(true);
      getExecutingAgents(agencyId).then(result => {
        const data = result.success ? result.data : [];
        setAgents(data);
        setLoadingAgents(false);
        if (result.success && data.length > 0) {
          setCachedAgents(agencyId, data);
        }
      }).catch(error => {
        toast.error('Failed to load agents: ' + error.message);
        setLoadingAgents(false);
      });
    }

    // Load cities - resolves independently
    if (cachedCitiesData?.length > 0) {
      setCities(cachedCitiesData);
    } else {
      setLoadingCities(true);
      getCities(agencyId).then(result => {
        const data = result.success ? result.data : [];
        setCities(data);
        setLoadingCities(false);
        if (result.success && data.length > 0) {
          setCachedCities(agencyId, data);
        }
      }).catch(error => {
        toast.error('Failed to load cities: ' + error.message);
        setLoadingCities(false);
      });
    }
  }, [agencyId, getCachedAgents, getCachedCities, setCachedAgents, setCachedCities]);

  // Handle field changes
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  // Handle void toggle - clears form fields like legacy makevoid() (ModalController.js:87-100)
  const handleVoidToggle = useCallback((checked) => {
    const newStatus = checked ? POWER_STATUS.VOID : POWER_STATUS.UNEXECUTED;
    if (checked) {
      // Clear all fields except powerNumber when voiding (matches legacy behavior)
      setFormData(prev => ({
        ...prev,
        powerStatusCd: newStatus,
        powerAmount: '',
        executionDate: null,
        executingAgentId: null,
        executingAgent: null,
        cityId: null,
        city: null,
        caseNumber: '',
        offense: '',
        defendantFirstName: '',
        defendantLastName: '',
        defendantDob: null,
        defendantSsn: '',
        scAmount: '',
        rateId: null
      }));
      // Clear display states for date/currency inputs
      setExecutionDateText('');
      setDobText('');
      setPowerAmountDisplay('');
      setScAmountDisplay('');
      setAgentSearch('');
      setCitySearch('');
      // Clear validation errors since void bypasses most validation
      setErrors({});
    } else {
      setFormData(prev => ({ ...prev, powerStatusCd: newStatus }));
    }
  }, []);

  // Load available rates for the power
  const loadRates = useCallback(async () => {
    const { powerNumber, executingState, executionDate, powerAmount, rateId } = formData;

    // Only load rates if we have all required data
    if (!powerNumber || !executingState || !executionDate || !powerAmount || parseFloat(powerAmount) <= 0) {
      console.debug('loadRates: skipping — missing required fields', { powerNumber, executingState, executionDate, powerAmount });
      return;
    }

    // Cancel any previous in-flight rate request
    if (rateAbortControllerRef.current) {
      rateAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    rateAbortControllerRef.current = abortController;

    try {
      setLoadingRates(true);
      const result = await getAvailableRates(
        powerNumber,
        executingState,
        executionDate,
        parseFloat(powerAmount)
      );

      // If this request was superseded, discard the response
      if (abortController.signal.aborted) return;

      if (result.success && result.data) {
        const rates = result.data.rates || [];
        setAvailableRates(rates);

        // Filter by amount range (client-side)
        const amount = parseFloat(powerAmount);
        const validRates = rates.filter(rate => {
          const minOk = !rate.minAmount || rate.minAmount <= amount;
          const maxOk = !rate.maxAmount || rate.maxAmount >= amount;
          return minOk && maxOk;
        });

        // Check if current rate is still valid
        const currentRateStillValid = rateId && validRates.some(r => r.rateId === rateId);

        if (!currentRateStillValid) {
          // Current rate is invalid or not selected, pick a new one
          if (result.data.defaultRateId && validRates.some(r => r.rateId === result.data.defaultRateId)) {
            handleFieldChange('rateId', result.data.defaultRateId);
          } else if (validRates.length > 0) {
            // Select first standard rate, or first rate if no standard rates
            const standardRates = validRates.filter(r => r.priority !== RATE_PRIORITY.VARIABLE);
            const rateToSelect = standardRates.length > 0 ? standardRates[0] : validRates[0];
            handleFieldChange('rateId', rateToSelect.rateId);
          }
        }
      } else {
        console.error('Failed to load rates:', result.error);
        setAvailableRates([]);
      }
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('Error loading rates:', error);
      setAvailableRates([]);
    } finally {
      if (!abortController.signal.aborted) {
        setLoadingRates(false);
      }
    }
  }, [formData, handleFieldChange]);

  // Keep ref current so the debounced effect always calls the latest version
  loadRatesRef.current = loadRates;

  // Handle rate selection change
  const handleRateChange = useCallback((rateId) => {
    handleFieldChange('rateId', rateId);

    // Find the selected rate to initialize proof attributes
    const newRate = filteredRates.find(r => r.rateId === rateId);

    // Initialize proof attribute values based on the new rate's requirements
    // Per legacy logic: create empty value entries for each proof attribute type
    if (newRate?.proofAttributeTypes?.length > 0) {
      const initialValues = newRate.proofAttributeTypes.map(attr => ({
        proofAttributeTypeId: attr.id,
        value: ''
      }));
      setProofAttributeValues(initialValues);
    } else {
      setProofAttributeValues([]);
    }
  }, [handleFieldChange, filteredRates]);

  // Handle proof attribute value change
  const handleProofAttributeChange = useCallback((proofAttributeTypeId, value) => {
    setProofAttributeValues(prev => {
      const existing = prev.findIndex(p => p.proofAttributeTypeId === proofAttributeTypeId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { proofAttributeTypeId, value };
        return updated;
      }
      return [...prev, { proofAttributeTypeId, value }];
    });
  }, []);

  // Initialize data when modal opens
  useEffect(() => {
    if (!isOpen) {
      return () => {
        setErrors({});
      };
    }

    if (!isEditMode) {
      setExecutionDateText('');
      setDobText('');
      setPowerAmountDisplay('');
      setScAmountDisplay('');
    }

    // Auto-focus first field after a short delay for modal animation
    const focusTimeout = focusPowerField();

    return () => clearTimeout(focusTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode]);

  // Handle preloaded agents and cities - separate effect to respond to prop changes
  useEffect(() => {
    if (!isOpen) return;

    let stale = false;

    // Use preloaded data if available
    const hasPreloadedAgents = preloadedAgents?.length > 0;
    const hasPreloadedCities = preloadedCities?.length > 0;

    if (hasPreloadedAgents) {
      setAgents(preloadedAgents);
      setLoadingAgents(false);
    }
    if (hasPreloadedCities) {
      setCities(preloadedCities);
      setLoadingCities(false);
    }

    // Only fetch if preloaded data is missing AND parent didn't already attempt the load
    // If dataPreloaded is true but arrays are empty, the load was attempted but failed — don't retry immediately
    if (!stale && !dataPreloaded && (!hasPreloadedAgents || !hasPreloadedCities)) {
      loadAgentsAndCities();
    }

    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preloadedAgents, preloadedCities]);

  // Load available powers when modal opens and agencyId is available
  useEffect(() => {
    if (isOpen && !isEditMode && agencyId) {
      if (preloadedPowers?.length > 0) {
        setAvailablePowers(preloadedPowers);
        setCachedPowers(agencyId, preloadedPowers);
        setLoadingPowers(false);
      } else {
        loadAvailablePowers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, agencyId, loadAvailablePowers, preloadedPowers]);

  // Load rates when power number, state, date, or amount change (debounced)
  useEffect(() => {
    if (!isOpen || isVoid) return;

    const { powerNumber, executingState, executionDate, powerAmount } = formData;

    // Only load rates if we have all required data
    if (!powerNumber || !executingState || !executionDate || !powerAmount || parseFloat(powerAmount) <= 0) {
      setAvailableRates([]);
      return;
    }

    // Debounce rate loading to avoid excessive API calls
    // Uses loadRatesRef to always call the latest version (avoids stale closure)
    const timeoutId = setTimeout(() => {
      loadRatesRef.current();
    }, 500);

    return () => clearTimeout(timeoutId);
    // Deps intentionally list individual formData fields — we only reload rates when these specific fields change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isVoid, formData.powerNumber, formData.executingState, formData.executionDate, formData.powerAmount]);

  // Re-select a valid rate when power amount changes and current rate is no longer valid
  useEffect(() => {
    if (!isOpen || isVoid || availableRates.length === 0) return;

    // If current rate is not in filtered list, auto-select a valid one
    if (formData.rateId && !filteredRates.some(r => r.rateId === formData.rateId)) {
      if (filteredRates.length > 0) {
        // Prefer standard rates over variable rates
        const standardRates = filteredRates.filter(r => r.priority !== RATE_PRIORITY.VARIABLE);
        const rateToSelect = standardRates.length > 0 ? standardRates[0] : filteredRates[0];
        handleRateChange(rateToSelect.rateId);
      } else {
        // No valid rates available for this amount
        handleFieldChange('rateId', null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isVoid, filteredRates, formData.rateId]);

  // Handle click outside and Escape key to close dropdowns
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // Close power dropdown if clicked outside
      if (powerDropdownRef.current && !powerDropdownRef.current.contains(event.target)) {
        setShowPowerDropdown(false);
      }
      // Close agent dropdown if clicked outside
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false);
      }
      // Close city dropdown if clicked outside
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showPowerDropdown || showAgentDropdown || showCityDropdown) {
          setShowPowerDropdown(false);
          setShowAgentDropdown(false);
          setShowCityDropdown(false);
          event.stopPropagation(); // Prevent modal from closing if dropdown was open
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isOpen, showPowerDropdown, showAgentDropdown, showCityDropdown]);

  // Derived booleans for edit-mode effect — avoids re-runs on array size changes
  const hasAgents = agents.length > 0;
  const hasCities = cities.length > 0;

  // Pre-fill form if editing existing power
  useEffect(() => {
    if (!isOpen || !isEditMode || !power || !hasAgents || !hasCities) {
      return;
    }

    let executingAgent = null;
    if (power.executingAgentId && power.executingAgentId !== 0) {
      executingAgent = agentMap.get(power.executingAgentId) || null;
    }
    if (!executingAgent && power.executingAgentFirstName && power.executingAgentLastName) {
      executingAgent = agentMap.get(`${power.executingAgentFirstName}_${power.executingAgentLastName}`) || null;
    }

    let city = power.powerReportCityId ? cityMap.get(power.powerReportCityId) || null : null;
    if (!city && power.cityName && power.stateCode) {
      city = cityMap.get(`${power.cityName}_${power.stateCode}`) || null;
    }

    const executionDateValue = power.executionDate ? new Date(power.executionDate) : null;
    const dobValue = power.defendantDob ? new Date(power.defendantDob) : null;

    setFormData({
      powerNumber: power.powerNumber || '',
      powerStatusCd: power.powerStatusCd || POWER_STATUS.UNEXECUTED,
      powerAmount: power.powerAmount || '',
      executionDate: executionDateValue,
      caseNumber: power.caseNumber || '',
      offense: (power.offense || '').toUpperCase(),
      executingAgentId: power.executingAgentId || null,
      executingAgent: executingAgent,
      cityId: city?.cityId || power.powerReportCityId || null,
      city: city,
      executingState: power.stateCode || '',
      defendantFirstName: (power.defendantFirstName || '').toUpperCase(),
      defendantLastName: (power.defendantLastName || '').toUpperCase(),
      defendantDob: dobValue,
      defendantSsn: power.defendantSsn ? formatSSN(power.defendantSsn) : '',
      scAmount: power.scamount || '',
      rateId: power.rateId || null  // Preserve rate selection when editing
    });

    // Initialize proof attribute values if the power has them
    if (power.proofAttributeValues?.length > 0) {
      setProofAttributeValues(power.proofAttributeValues.map(v => ({
        proofAttributeTypeId: v.proofAttributeTypeId,
        value: v.value || ''
      })));
    }

    setExecutionDateText(formatDateForDisplay(executionDateValue));
    setDobText(formatDateForDisplay(dobValue));
    setPowerAmountDisplay(power.powerAmount ? formatCurrencyInput(power.powerAmount) : '');
    setScAmountDisplay(power.scamount ? formatCurrencyInput(power.scamount) : '');
    if (executingAgent) setAgentSearch(`${executingAgent.firstName} ${executingAgent.lastName}`);
    if (city) setCitySearch(`${toTitleCase(city.name)}, ${city.stateCode}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, power, hasAgents, hasCities]);

  // Handle power selection
  const handlePowerSelected = useCallback((selectedPower) => {
    if (selectedPower) {
      // Check if this power is already in the current report
      if (isPowerInCurrentReport(selectedPower.powerNumber)) {
        setPowerSearch(selectedPower.powerNumber);
        setFormData(prev => ({ ...prev, powerNumber: selectedPower.powerNumber }));
        setErrors(prev => ({ ...prev, powerNumber: 'This power is already in this report.' }));
        setShowPowerDropdown(false);
        setHighlightedPowerIndex(-1);
        return;
      }

      setFormData(prev => ({
        ...prev,
        powerNumber: selectedPower.powerNumber,
        // Preserve void status if already set (legacy behavior: PowerReport.js line 2178)
        powerStatusCd: prev.powerStatusCd === POWER_STATUS.VOID
          ? POWER_STATUS.VOID
          : (selectedPower.powerStatusCd || POWER_STATUS.UNEXECUTED)
      }));
      setSelectedPowerDenomination(selectedPower.denomination || 0);
      setPowerSearch(selectedPower.powerNumber);
    } else {
      setFormData(prev => ({
        ...prev,
        powerNumber: '',
        // Preserve void status if already set
        powerStatusCd: prev.powerStatusCd === POWER_STATUS.VOID
          ? POWER_STATUS.VOID
          : POWER_STATUS.UNEXECUTED
      }));
      setSelectedPowerDenomination(0);
    }
    setShowPowerDropdown(false);
    setHighlightedPowerIndex(-1);
    // Clear any status lookup error when a power is selected from dropdown
    setPowerStatusLookup(null);
    setErrors(prev => ({ ...prev, powerNumber: null }));
    // Reset threshold acknowledgment when power changes
    setThresholdAcknowledged(false);
  }, [isPowerInCurrentReport]);

  // Handle agent selection
  const handleAgentSelected = useCallback((selectedAgent) => {
    if (selectedAgent) {
      setFormData(prev => ({
        ...prev,
        executingAgentId: selectedAgent.executingAgentId,
        executingAgent: selectedAgent
      }));
      setAgentSearch(`${selectedAgent.firstName} ${selectedAgent.lastName}`);
    } else {
      setFormData(prev => ({
        ...prev,
        executingAgentId: null,
        executingAgent: null
      }));
    }
    setShowAgentDropdown(false);
    setHighlightedAgentIndex(-1);
  }, []);

  // Handle city selection
  const handleCitySelected = useCallback((selectedCity) => {
    if (selectedCity) {
      setFormData(prev => ({
        ...prev,
        cityId: selectedCity.cityId,
        city: selectedCity,
        executingState: selectedCity.stateCode
      }));
      setCitySearch(`${toTitleCase(selectedCity.name)}, ${selectedCity.stateCode}`);
    } else {
      setFormData(prev => ({
        ...prev,
        cityId: null,
        city: null,
        executingState: ''
      }));
    }
    setShowCityDropdown(false);
    setHighlightedCityIndex(-1);
  }, []);

  // Handle keyboard navigation for city dropdown
  const handleCityKeyDown = useCallback((e) => {
    if (!showCityDropdown || filteredCities.length === 0) {
      // Open dropdown on arrow down even if closed
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowCityDropdown(true);
        setHighlightedCityIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedCityIndex(prev =>
          prev < filteredCities.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedCityIndex(prev =>
          prev > 0 ? prev - 1 : filteredCities.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedCityIndex >= 0 && highlightedCityIndex < filteredCities.length) {
          handleCitySelected(filteredCities[highlightedCityIndex]);
        }
        break;
      case 'Tab':
        // Allow tab to move focus, select current item if highlighted
        if (highlightedCityIndex >= 0 && highlightedCityIndex < filteredCities.length) {
          handleCitySelected(filteredCities[highlightedCityIndex]);
        }
        setShowCityDropdown(false);
        break;
      case 'Escape':
        setShowCityDropdown(false);
        setHighlightedCityIndex(-1);
        break;
      default:
        break;
    }
  }, [showCityDropdown, filteredCities, highlightedCityIndex, handleCitySelected]);

  // Handle keyboard navigation for agent dropdown
  const handleAgentKeyDown = useCallback((e) => {
    if (!showAgentDropdown || filteredAgents.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowAgentDropdown(true);
        setHighlightedAgentIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedAgentIndex(prev =>
          prev < filteredAgents.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedAgentIndex(prev =>
          prev > 0 ? prev - 1 : filteredAgents.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedAgentIndex >= 0 && highlightedAgentIndex < filteredAgents.length) {
          handleAgentSelected(filteredAgents[highlightedAgentIndex]);
        }
        break;
      case 'Tab':
        if (highlightedAgentIndex >= 0 && highlightedAgentIndex < filteredAgents.length) {
          handleAgentSelected(filteredAgents[highlightedAgentIndex]);
        }
        setShowAgentDropdown(false);
        break;
      case 'Escape':
        setShowAgentDropdown(false);
        setHighlightedAgentIndex(-1);
        break;
      default:
        break;
    }
  }, [showAgentDropdown, filteredAgents, highlightedAgentIndex, handleAgentSelected]);

  // Handle keyboard navigation for power dropdown
  const handlePowerKeyDown = useCallback((e) => {
    if (!showPowerDropdown || filteredPowers.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowPowerDropdown(true);
        setHighlightedPowerIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedPowerIndex(prev =>
          prev < filteredPowers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedPowerIndex(prev =>
          prev > 0 ? prev - 1 : filteredPowers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedPowerIndex >= 0 && highlightedPowerIndex < filteredPowers.length) {
          handlePowerSelected(filteredPowers[highlightedPowerIndex]);
        }
        break;
      case 'Tab':
        if (highlightedPowerIndex >= 0 && highlightedPowerIndex < filteredPowers.length) {
          handlePowerSelected(filteredPowers[highlightedPowerIndex]);
        }
        setShowPowerDropdown(false);
        break;
      case 'Escape':
        setShowPowerDropdown(false);
        setHighlightedPowerIndex(-1);
        break;
      default:
        break;
    }
  }, [showPowerDropdown, filteredPowers, highlightedPowerIndex, handlePowerSelected]);

  // Handle power number blur - validate typed power numbers not selected from dropdown
  const handlePowerBlur = useCallback(async () => {
    // If power already selected from dropdown, nothing to do
    if (formData.powerNumber) {
      setPowerStatusLookup(null);
      return;
    }

    // If search is empty or wrong length, let standard validation handle it
    if (!powerSearch || powerSearch.length !== VALIDATION_RULES.POWER_NUMBER_LENGTH) {
      setPowerStatusLookup(null);
      return;
    }

    // Check if this power is already in the current report
    if (isPowerInCurrentReport(powerSearch)) {
      setFormData(prev => ({ ...prev, powerNumber: powerSearch }));
      setErrors(prev => ({ ...prev, powerNumber: 'This power is already in this report.' }));
      return;
    }

    // Check for exact match in available powers (user typed a valid power but didn't select)
    const exactMatch = availablePowers.find(p => p.powerNumber === powerSearch);
    if (exactMatch) {
      handlePowerSelected(exactMatch);
      setPowerStatusLookup(null);
      setErrors(prev => ({ ...prev, powerNumber: null }));
      return;
    }

    // Power not in available list - look up its status via API
    try {
      const result = await getPowerDetails(powerSearch);
      if (result.success && result.data) {
        const status = result.data.powerStatus;
        const availableStatuses = ['U', 'UF', 'UJ', 'UD'];

        if (status === 'UT') {
          // Transfer bond power — determine if user is the Posting Agent (PA) or Receiving Agent (RA)
          // previousAgentId = PA (original owner who posted the transfer)
          // agencyId = RA (current owner after transfer)
          const previousAgentId = result.data.previousAgentId;
          const isPostingAgent = previousAgentId && agencyId &&
                                 Number(previousAgentId) === Number(agencyId);
          const isReceivingAgent = result.data.agencyId && agencyId &&
                                   Number(result.data.agencyId) === Number(agencyId);

          setFormData(prev => ({ ...prev, powerNumber: powerSearch }));
          if (isPostingAgent) {
            setPowerStatusLookup({ found: true, status, isTransferBond: true, isPostingAgent: true });
            setErrors(prev => ({
              ...prev,
              powerNumber: 'This power is a transfer bond posted by your agency and can be added upon submission. Please select the cancel button or proceed with adding another power to this report.'
            }));
          } else if (isReceivingAgent) {
            setPowerStatusLookup({ found: true, status, isTransferBond: true, isPostingAgent: false });
            setErrors(prev => ({
              ...prev,
              powerNumber: 'This power is a transfer bond received by your agency and can be added upon submission. Please select the cancel button or proceed with adding another power to this report.'
            }));
          } else {
            setPowerStatusLookup({ found: true, status, isTransferBond: false });
            setErrors(prev => ({
              ...prev,
              powerNumber: 'This power is not available for this agency.'
            }));
          }
        } else if (availableStatuses.includes(status)) {
          // Power has an available status but isn't in dropdown - check if it's in another active report
          const activeCheck = await checkPowerActiveReport(powerSearch, reportId);
          if (activeCheck.success && activeCheck.data?.isInActiveReport) {
            setFormData(prev => ({ ...prev, powerNumber: powerSearch }));
            setPowerStatusLookup({ found: true, status, inActiveReport: true, reportNumber: activeCheck.data.reportNumber });
            setErrors(prev => ({
              ...prev,
              powerNumber: `This power is in active report: ${activeCheck.data.reportNumber}. Please either remove it from that report or submit that report to execute this power.`
            }));
          } else {
            setFormData(prev => ({ ...prev, powerNumber: powerSearch }));
            setPowerStatusLookup({ found: true, status, inActiveReport: false });
            setErrors(prev => ({
              ...prev,
              powerNumber: 'This power is not available for this agency.'
            }));
          }
        } else {
          // Non-available status (E, T, F, J, D) - show status error
          setFormData(prev => ({ ...prev, powerNumber: powerSearch }));
          setPowerStatusLookup({ found: true, status });
          setErrors(prev => ({
            ...prev,
            powerNumber: `This power is in ${status} status and can not be reported again.`
          }));
        }
      } else {
        setPowerStatusLookup({ found: false });
        setErrors(prev => ({
          ...prev,
          powerNumber: 'Power number not found'
        }));
      }
    } catch {
      setPowerStatusLookup({ found: false });
      setErrors(prev => ({
        ...prev,
        powerNumber: 'Unable to verify power number'
      }));
    }
  }, [formData.powerNumber, powerSearch, availablePowers, handlePowerSelected, reportId, isPowerInCurrentReport]);

  // Scroll highlighted item into view when navigation changes
  useEffect(() => {
    if (highlightedCityIndex >= 0 && cityListRef.current) {
      const item = cityListRef.current.children[highlightedCityIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedCityIndex]);

  useEffect(() => {
    if (highlightedAgentIndex >= 0 && agentListRef.current) {
      const item = agentListRef.current.children[highlightedAgentIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedAgentIndex]);

  useEffect(() => {
    if (highlightedPowerIndex >= 0 && powerListRef.current) {
      const item = powerListRef.current.children[highlightedPowerIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedPowerIndex]);

  // Handle date text changes
  const handleDateTextChange = useCallback((field, value) => {
    const formatted = formatDateInputText(value);
    if (field === 'executionDate') {
      setExecutionDateText(formatted);
    } else if (field === 'defendantDob') {
      setDobText(formatted);
    }
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  // Handle date text blur
  const handleDateTextBlur = useCallback((field) => {
    const textValue = field === 'executionDate' ? executionDateText : dobText;

    if (!textValue) {
      setFormData(prev => ({ ...prev, [field]: null }));
      return;
    }

    const parsedDate = parseDateInput(textValue);

    if (parsedDate) {
      setFormData(prev => ({ ...prev, [field]: parsedDate }));
      const fullDateText = formatDateForDisplay(parsedDate);
      if (field === 'executionDate') {
        setExecutionDateText(fullDateText);
      } else if (field === 'defendantDob') {
        setDobText(fullDateText);
      }
    }
  }, [executionDateText, dobText]);

  // Handle currency input changes with formatting
  const handleCurrencyChange = useCallback((field, value, setDisplayFn) => {
    const formatted = formatCurrencyInput(value);
    setDisplayFn(formatted);
    // Store raw value without commas in form state
    const rawValue = parseCurrencyInput(formatted);
    setFormData(prev => ({ ...prev, [field]: rawValue }));
    setErrors(prev => ({ ...prev, [field]: null }));
    // Reset threshold acknowledgment when amount changes
    if (field === 'powerAmount') {
      setThresholdAcknowledged(false);
    }
  }, []);

  // Filter to only allow letters, spaces, hyphens, and apostrophes (for names)
  const lettersOnly = useCallback((str) => {
    if (!str) return str;
    return str.replace(/[^a-zA-Z0-9\s\-']/g, '');
  }, []);

  // Validate a single field
  const validateField = useCallback((field) => {
    const value = formData[field];
    let error = null;

    switch (field) {
      case 'powerNumber':
        if (!value || value.length !== VALIDATION_RULES.POWER_NUMBER_LENGTH) {
          error = `Power number must be exactly ${VALIDATION_RULES.POWER_NUMBER_LENGTH} characters`;
        } else if (isPowerInCurrentReport(value)) {
          error = 'This power is already in this report.';
        } else if (powerStatusLookup?.found && powerStatusLookup.isTransferBond && powerStatusLookup.isPostingAgent) {
          error = 'This power is a transfer bond posted by your agency and can be added upon submission. Please select the cancel button or proceed with adding another power to this report.';
        } else if (powerStatusLookup?.found && powerStatusLookup.isTransferBond && !powerStatusLookup.isPostingAgent) {
          error = 'This power is a transfer bond received by your agency and can be added upon submission. Please select the cancel button or proceed with adding another power to this report.';
        } else if (powerStatusLookup?.found && powerStatusLookup.isTransferBond === false) {
          // UT power that belongs to a different agency
          error = 'This power is not available for this agency.';
        } else if (powerStatusLookup?.found && powerStatusLookup.inActiveReport) {
          error = `This power is in active report: ${powerStatusLookup.reportNumber}. Please either remove it from that report or submit that report to execute this power.`;
        } else if (powerStatusLookup?.found && powerStatusLookup.inActiveReport === false) {
          // Available status but not in dropdown and not in another active report
          error = 'This power is not available for this agency.';
        } else if (powerStatusLookup?.found && powerStatusLookup.status) {
          // Power exists but is not in an available status (e.g., E, T, F, J, D)
          error = `This power is in ${powerStatusLookup.status} status and can not be reported again.`;
        } else if (powerStatusLookup && !powerStatusLookup.found) {
          error = 'Power number not found';
        }
        break;
      case 'powerAmount':
        if (!isVoid && (!value || parseFloat(value) <= 0)) {
          error = 'Power amount must be greater than 0';
        }
        break;
      case 'executionDate':
        if (!isVoid) {
          if (!value) {
            error = 'Execution date is required';
          } else {
            const executionDate = new Date(value);
            const now = new Date();
            const maxYearsAgo = new Date();
            maxYearsAgo.setFullYear(now.getFullYear() - VALIDATION_RULES.MAX_EXECUTION_DATE_YEARS_BACK);
            if (executionDate > now) {
              error = 'Execution date cannot be in the future';
            } else if (executionDate < maxYearsAgo) {
              error = `Execution date cannot be more than ${VALIDATION_RULES.MAX_EXECUTION_DATE_YEARS_BACK} years in the past`;
            }
          }
        }
        break;
      case 'executingAgent':
      case 'executingAgentId':
        if (!isVoid && !formData.executingAgentId) {
          error = 'Executing agent is required';
        }
        break;
      case 'city':
      case 'cityId':
        if (!isVoid && !formData.city) {
          error = 'Court city is required';
        }
        break;
      case 'defendantFirstName':
        if (!isVoid && !value) {
          error = 'Defendant first name is required';
        }
        break;
      case 'defendantLastName':
        if (!isVoid && !value) {
          error = 'Defendant last name is required';
        }
        break;
      case 'defendantDob':
        if (value) {
          const dob = new Date(value);
          const now = new Date();
          const age = now.getFullYear() - dob.getFullYear();
          if (age < VALIDATION_RULES.MIN_DEFENDANT_AGE) {
            error = `Defendant must be at least ${VALIDATION_RULES.MIN_DEFENDANT_AGE} years old`;
          }
        }
        break;
      case 'defendantSsn':
        if (value && !VALIDATION_RULES.SSN_PATTERN.test(value)) {
          error = 'SSN must be in format XXX-XX-XXXX';
        }
        break;
      case 'scAmount':
        if (isSouthCarolina && formData.executingState?.toUpperCase() === 'SC' && !isVoid) {
          if (!value || parseFloat(value) <= 0) {
            error = 'Actual SC Premium is required and must be greater than $0';
          }
        }
        break;
      default:
        break;
    }

    return error;
  }, [formData, isVoid, isSouthCarolina, powerStatusLookup, isPowerInCurrentReport]);

  // Handle field blur validation
  const handleFieldBlur = useCallback((field) => {
    const error = validateField(field);
    setErrors(prev => ({ ...prev, [field]: error }));

    // Check if power amount exceeds denomination on blur (legacy behavior)
    // Skip if user already acknowledged the warning for this amount
    if (field === 'powerAmount' && !isEditMode && !thresholdAcknowledged && selectedPowerDenomination > 0) {
      const powerAmount = parseFloat(formData.powerAmount) || 0;
      if (powerAmount > selectedPowerDenomination) {
        setShowThresholdDialog(true);
      }
    }
  }, [validateField, isEditMode, thresholdAcknowledged, selectedPowerDenomination, formData.powerAmount]);

  // Validate entire form
  const validateForm = useCallback(() => {
    const fieldsToValidate = [
      'powerNumber', 'powerAmount', 'executionDate', 'executingAgent',
      'city', 'defendantFirstName', 'defendantLastName', 'defendantDob',
      'defendantSsn', 'scAmount'
    ];

    const newErrors = {};
    fieldsToValidate.forEach(field => {
      const error = validateField(field);
      if (error) newErrors[field] = error;
    });

    // Validate required proof attributes for variable rates
    if (!isVoid && selectedRate?.proofAttributeTypes?.length > 0) {
      const requiredAttributes = selectedRate.proofAttributeTypes.filter(attr => attr.required);
      requiredAttributes.forEach(attr => {
        const value = proofAttributeValues.find(v => v.proofAttributeTypeId === attr.id)?.value;
        if (!value || !value.trim()) {
          newErrors[`proofAttribute_${attr.id}`] = `${attr.name} is required for this rate`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [validateField, isVoid, selectedRate, proofAttributeValues]);

  // Focus the power number input field
  const focusPowerField = useCallback(() => {
    isInitialFocusRef.current = true;
    const focusTimeout = setTimeout(() => {
      if (firstFieldRef.current) {
        firstFieldRef.current.focus();
        setTimeout(() => {
          isInitialFocusRef.current = false;
        }, 50);
      }
    }, 100);
    return focusTimeout;
  }, []);

  // Reset form for new entry
  const resetFormForNew = useCallback(() => {
    setFormData({ ...INITIAL_FORM_DATA, executionDate: null });
    setSelectedPowerDenomination(0);
    setErrors({});
    setExecutionDateText('');
    setDobText('');
    setPowerAmountDisplay('');
    setScAmountDisplay('');
    setPowerSearch('');
    setAgentSearch('');
    setCitySearch('');
    // Reset dropdown highlight indices
    setHighlightedPowerIndex(-1);
    setHighlightedAgentIndex(-1);
    setHighlightedCityIndex(-1);
    // Clear rate-related state
    setAvailableRates([]);
    setProofAttributeValues([]);
    setPowerStatusLookup(null);
    loadAvailablePowers();
    // Re-focus the power number field for next entry
    focusPowerField();
    toast.success('Ready for new power entry');
  }, [loadAvailablePowers, focusPowerField]);

  // Reset form for copy
  const resetFormForCopy = useCallback((savedFormState) => {
    const newFormData = {
      ...savedFormState,
      powerNumber: '',
      powerStatusCd: POWER_STATUS.UNEXECUTED,
      caseNumber: '',
      offense: '',
      powerAmount: '',  // Don't copy bond amount
      scAmount: '',     // Don't copy SC premium
      rateId: null      // Reset rate on copy since power number changes
    };

    setFormData(newFormData);
    setExecutionDateText(formatDateForDisplay(savedFormState.executionDate));
    setDobText(formatDateForDisplay(savedFormState.defendantDob));
    setPowerAmountDisplay('');  // Clear bond amount display
    setScAmountDisplay('');     // Clear SC premium display
    setPowerSearch('');
    setSelectedPowerDenomination(0);
    setErrors({});
    // Reset dropdown highlight indices
    setHighlightedPowerIndex(-1);
    setHighlightedAgentIndex(-1);
    setHighlightedCityIndex(-1);
    // Clear rate-related state since power number changes
    setAvailableRates([]);
    setProofAttributeValues([]);
    setPowerStatusLookup(null);
    loadAvailablePowers();
    // Re-focus the power number field for next entry
    focusPowerField();
    toast.success('Form copied - select new power number');
  }, [loadAvailablePowers, focusPowerField]);

  // Handle post-save actions
  const handlePostSaveAction = (action, savedFormState, savedResult) => {
    switch (action) {
      case 'close':
        handleClose();
        break;
      case 'new':
        resetFormForNew();
        break;
      case 'copy':
        resetFormForCopy(savedFormState);
        break;
      case 'proof':
        {
          // Capture the proof dialog callback from the ref BEFORE closing the modal,
          // to ensure we have the latest reference regardless of closure staleness
          const openProofDialog = onOpenProofDialogRef.current;
          handleClose();
          if (openProofDialog && savedResult) {
            // Use setTimeout to ensure the AddEditPowerModal has fully closed
            // before opening the ProofUploadModal, preventing React state batching
            // from swallowing the proof dialog open state
            setTimeout(() => {
              openProofDialog({
                powerReportPowerId: savedResult.powerReportPowerId,
                powerNumber: savedResult.powerNumber || savedFormState.powerNumber,
                hasProof: savedResult.hasProof || false
              });
            }, 0);
          }
        }
        break;
      default:
        handleClose();
    }
  };

  // Perform save operation
  const performSave = useCallback(async (action) => {
    // Block concurrent saves from a rapid double/triple-click before React
    // flushes the disabled-button re-render. Prevents duplicate power entries.
    if (saveInProgressRef.current) return;
    saveInProgressRef.current = true;

    const savedFormState = { ...formData };

    try {
      setLoading(true);
      setLoadingAction(action);

      // For void powers, send current date as executionDate (legacy behavior: ElectronicReporting.cs:539
      // sets ExecutionDate = DateTime.Now on the server for void powers, but the modern API requires
      // a valid DateTime in the JSON payload before business logic runs)
      const executionDate = isVoid
        ? formatDateForInput(new Date())
        : (formatDateForInput(formData.executionDate) || null);

      const requestData = {
        powerReportId: reportId,
        powerNumber: formData.powerNumber,
        powerStatusCd: formData.powerStatusCd,
        powerAmount: parseFloat(formData.powerAmount) || 0,
        executionDate,
        caseNumber: formData.caseNumber || null,
        offense: formData.offense?.toUpperCase() || null,
        executingAgentId: formData.executingAgentId || 0,
        cityId: formData.cityId || 0,
        cityName: formData.city?.name || '',
        executingState: formData.executingState || '',
        defendantFirstName: formData.defendantFirstName?.toUpperCase() || '',
        defendantLastName: formData.defendantLastName?.toUpperCase() || '',
        defendantDob: formatDateForInput(formData.defendantDob) || null,
        defendantSsn: formData.defendantSsn || null,
        scamount: formData.scAmount ? parseFloat(formData.scAmount) : null,
        // Variable Rate support
        rateId: formData.rateId || null,
        proofAttributeValues: proofAttributeValues.filter(v => v.value) || null
      };

      let apiResult;
      if (isEditMode) {
        apiResult = await updatePower(power.powerReportPowerId, requestData);
      } else {
        apiResult = await addPowerToReport(requestData);
      }

      if (apiResult.success) {
        toast.success(isEditMode ? 'Power updated successfully' : 'Power added successfully');

        // Invalidate powers cache when a new power is added (it's now consumed)
        if (!isEditMode) {
          invalidatePowersCache(agencyId);
        }

        // Set form state first (copy/new reset the form, close/proof close the modal)
        handlePostSaveAction(action, savedFormState, apiResult.data);

        // Then notify parent to refresh the powers table in the background.
        // This runs after handlePostSaveAction so copy/new form state is already set
        // before the parent re-renders. The parent uses a silent refresh to avoid
        // unmounting the modal.
        if (onSave) {
          onSave(apiResult.data);
        }
      } else {
        toast.error('Failed to save power: ' + (apiResult.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to save power: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingAction(null);
      saveInProgressRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, reportId, isEditMode, power, onSave, agencyId, invalidatePowersCache]);

  // Handle save with action type
  const handleSave = useCallback(async (action = 'close') => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    // Show threshold dialog only if not already acknowledged
    const powerAmount = parseFloat(formData.powerAmount) || 0;
    if (!isEditMode && !thresholdAcknowledged && selectedPowerDenomination > 0 && powerAmount > selectedPowerDenomination) {
      setPendingSaveAction(action);
      setShowThresholdDialog(true);
      return;
    }

    await performSave(action);
  }, [validateForm, formData.powerAmount, isEditMode, thresholdAcknowledged, selectedPowerDenomination, performSave]);

  // Handle threshold confirmation
  const handleThresholdConfirm = useCallback(async () => {
    setShowThresholdDialog(false);
    setThresholdAcknowledged(true); // User acknowledged, don't show again for this amount
    // Only save if triggered from save button (pendingSaveAction is set)
    // If triggered from blur, just close the dialog and let user continue
    if (pendingSaveAction) {
      await performSave(pendingSaveAction);
      setPendingSaveAction(null);
    }
  }, [performSave, pendingSaveAction]);

  const handleThresholdCancel = useCallback(() => {
    setShowThresholdDialog(false);
    setPendingSaveAction(null);
    // Clear the power amount when user cancels (legacy behavior)
    setFormData(prev => ({ ...prev, powerAmount: '' }));
    setPowerAmountDisplay('');
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    // Cancel any in-flight rate request
    if (rateAbortControllerRef.current) {
      rateAbortControllerRef.current.abort();
      rateAbortControllerRef.current = null;
    }
    setFormData({ ...INITIAL_FORM_DATA, executionDate: null });
    setSelectedPowerDenomination(0);
    setThresholdAcknowledged(false);
    setErrors({});
    setExecutionDateText('');
    setDobText('');
    setPowerAmountDisplay('');
    setScAmountDisplay('');
    setPowerSearch('');
    setAgentSearch('');
    setCitySearch('');
    // Reset dropdown highlight indices
    setHighlightedPowerIndex(-1);
    setHighlightedAgentIndex(-1);
    setHighlightedCityIndex(-1);
    // Clear rate-related state
    setAvailableRates([]);
    setProofAttributeValues([]);
    onClose();
  }, [onClose]);

  // Shared Rate selector markup. Renders the same <select> whether it lives
  // in its own grid slot (for standard rates) or inside the amber "Variable Rate"
  // card (when a variable rate is selected).
  const rateSelectorMarkup = (
    <>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Rate <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <select
          value={formData.rateId || ''}
          onChange={(e) => handleRateChange(e.target.value ? parseInt(e.target.value) : null)}
          disabled={loadingRates}
          className={`w-full px-4 py-2 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300 dark:border-gray-600 ${loadingRates ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'} appearance-none`}
        >
          {filteredRates.filter(r => r.priority !== RATE_PRIORITY.VARIABLE).map(rate => {
            const display = formatRateDisplay(rate);
            return (
              <option key={rate.rateId} value={rate.rateId}>
                {display.fullDisplay}
              </option>
            );
          })}
          {filteredRates.some(r => r.priority === RATE_PRIORITY.VARIABLE) && (
            <option disabled>── Variable Rates ──</option>
          )}
          {filteredRates.filter(r => r.priority === RATE_PRIORITY.VARIABLE).map(rate => {
            const display = formatRateDisplay(rate);
            return (
              <option key={rate.rateId} value={rate.rateId}>
                {display.fullDisplay}
              </option>
            );
          })}
        </select>
        {loadingRates ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <OathSpinner size="sm" />
          </div>
        ) : (
          <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none text-gray-500 dark:text-gray-400" />
        )}
      </div>
    </>
  );

  const isVariableRate = selectedRate?.priority === RATE_PRIORITY.VARIABLE;

  // Rate description — rendered as a subtle full-width footnote inside the amber
  // card so a long description doesn't inflate the Rate cell and misalign the
  // proof-attribute inputs next to it. Only shows for contract-enabled users.
  const rateDescriptionText = (selectedRate && canAccessContract)
    ? (selectedRate.description || (
        selectedRate.rateTypeCode === RATE_TYPE.PERCENTAGE
          ? `${selectedRate.rate1}% of bond amount${selectedRate.additionalRate ? ` + ${selectedRate.additionalRateTypeCode === RATE_TYPE.FLAT ? '$' : ''}${selectedRate.additionalRate}${selectedRate.additionalRateTypeCode === RATE_TYPE.PERCENTAGE ? '%' : ''}` : ''}`
          : `Flat rate: $${selectedRate.rate1?.toLocaleString() || '0'}`
      ))
    : null;

  return (
    <>
      <OathModal
        isOpen={isOpen}
        onClose={handleClose}
        title={isEditMode ? 'Edit Power' : 'Add Power to Report'}
        size="xl"
        className="sm:max-w-6xl"
        allowOverflow
        persistent={true}
        footer={
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full">
            {isEditMode ? (
              <OathButton
                onClick={() => handleSave('close')}
                disabled={loading}
                className="w-full sm:w-auto sm:order-[2]"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <OathSpinner size="sm" />
                    Updating...
                  </span>
                ) : (
                  'Update Power'
                )}
              </OathButton>
            ) : (
              <>
                <OathButton
                  variant="outline"
                  onClick={() => handleSave('new')}
                  disabled={loading}
                  className="w-full sm:w-auto sm:order-[3]"
                  size="sm"
                >
                  {loadingAction === 'new' ? (
                    <span className="flex items-center gap-2">
                      <OathSpinner size="sm" />
                      Saving...
                    </span>
                  ) : (
                    'Save + New'
                  )}
                </OathButton>
                <OathButton
                  variant="outline"
                  onClick={() => handleSave('copy')}
                  disabled={loading || isVoid}
                  className="w-full sm:w-auto sm:order-[2]"
                  size="sm"
                >
                  {loadingAction === 'copy' ? (
                    <span className="flex items-center gap-2">
                      <OathSpinner size="sm" />
                      Saving...
                    </span>
                  ) : (
                    'Save + Copy'
                  )}
                </OathButton>
                <OathButton
                  variant="outline"
                  onClick={() => handleSave('proof')}
                  disabled={loading}
                  className="w-full sm:w-auto sm:order-[4]"
                  size="sm"
                >
                  {loadingAction === 'proof' ? (
                    <span className="flex items-center gap-2">
                      <OathSpinner size="sm" />
                      Saving...
                    </span>
                  ) : (
                    'Save + Proof'
                  )}
                </OathButton>
                <OathButton
                  variant="secondary"
                  onClick={() => handleSave('close')}
                  disabled={loading}
                  className="w-full sm:w-auto sm:order-[5]"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <OathSpinner size="sm" />
                      Saving...
                    </span>
                  ) : (
                    'Save + Close'
                  )}
                </OathButton>
              </>
            )}

            <OathButton
              variant="danger"
              onClick={handleClose}
              disabled={loading}
              className="w-full sm:w-auto order-last sm:order-[0]"
            >
              Cancel
            </OathButton>

            <div className="flex-1 hidden sm:block sm:order-[1]" />
          </div>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {/* Power Number and Void Toggle */}
          <div className="contents">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Power Number *
              </label>
              {isEditMode ? (
                <div>
                  <input
                    type="text"
                    value={formData.powerNumber}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Power number cannot be changed when editing
                  </p>
                </div>
              ) : (
                <div className="relative" ref={powerDropdownRef}>
                  <input
                    ref={firstFieldRef}
                    type="text"
                    value={powerSearch}
                    onChange={(e) => {
                      setPowerSearch(e.target.value);
                      setShowPowerDropdown(true);
                      setHighlightedPowerIndex(0);
                      // Clear any previous status lookup when user changes the input
                      setPowerStatusLookup(null);
                      // Clear formData.powerNumber so it gets re-evaluated on blur
                      if (formData.powerNumber) {
                        setFormData(prev => ({ ...prev, powerNumber: '' }));
                      }
                    }}
                    onFocus={() => {
                      // Don't open dropdown on initial auto-focus
                      if (!isInitialFocusRef.current) {
                        setShowPowerDropdown(true);
                        setHighlightedPowerIndex(0);
                      }
                    }}
                    onBlur={handlePowerBlur}
                    onKeyDown={handlePowerKeyDown}
                    placeholder="Search power number..."
                    className={`w-full px-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.powerNumber ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700`}
                  />
                  {loadingPowers && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <OathSpinner size="sm" />
                    </div>
                  )}
                  {showPowerDropdown && filteredPowers.length > 0 && (
                    <div
                      ref={powerListRef}
                      className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                    >
                      {filteredPowers.map((p, index) => (
                        <button
                          key={p.powerNumber}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handlePowerSelected(p)}
                          className={`w-full px-4 py-2 text-left ${
                            index === highlightedPowerIndex
                              ? 'bg-primary/10 dark:bg-primary/20'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span className="font-mono text-gray-900 dark:text-white">{p.powerNumber}</span>
                          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                            ${p.denomination?.toLocaleString() || 'N/A'}
                            {p.agencyName && ` - ${p.agencyName}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {errors.powerNumber && (
                    <p className="text-xs text-red-500 mt-1">{errors.powerNumber}</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Void
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isVoid}
                  onChange={(e) => handleVoidToggle(e.target.checked)}
                  disabled={isEditMode}
                  className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">Mark as void</span>
              </label>
            </div>
          </div>

          {/* Execution Date and Bond Amount */}
          <div className="contents">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Execution Date *
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={executionDateText}
                  onChange={(e) => handleDateTextChange('executionDate', e.target.value)}
                  onBlur={() => handleDateTextBlur('executionDate')}
                  disabled={isVoid}
                  placeholder="MM/DD/YYYY"
                  maxLength={10}
                  autoComplete="off"
                  className={`w-full px-4 py-2 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.executionDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => executionDatePickerRef.current?.showPicker()}
                  disabled={isVoid}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <CalendarIcon className="h-5 w-5" />
                </button>
                <input
                  type="date"
                  ref={executionDatePickerRef}
                  tabIndex={-1}
                  aria-hidden="true"
                  max={formatDateForInput(new Date())}
                  value={formatDateForInput(formData.executionDate) || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const dateValue = new Date(e.target.value + 'T00:00:00');
                      handleFieldChange('executionDate', dateValue);
                      setExecutionDateText(formatDateForDisplay(dateValue));
                    }
                  }}
                  className="sr-only"
                />
              </div>
              {errors.executionDate ? (
                <p className="text-xs text-red-500 mt-1">{errors.executionDate}</p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">MM/DD/YYYY (2-digit year OK)</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bond Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={powerAmountDisplay}
                  onChange={(e) => handleCurrencyChange('powerAmount', e.target.value, setPowerAmountDisplay)}
                  onBlur={() => handleFieldBlur('powerAmount')}
                  disabled={isVoid}
                  placeholder="0.00"
                  className={`w-full pl-8 pr-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.powerAmount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                />
              </div>
              {errors.powerAmount && (
                <p className="text-xs text-red-500 mt-1">{errors.powerAmount}</p>
              )}
            </div>
          </div>

          {/* Defendant Name */}
          <div className="contents">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Defendant First Name *
              </label>
              <input
                type="text"
                value={formData.defendantFirstName}
                onChange={(e) => handleFieldChange('defendantFirstName', lettersOnly(e.target.value).toUpperCase())}
                onBlur={() => handleFieldBlur('defendantFirstName')}
                disabled={isVoid}
                maxLength={VALIDATION_RULES.MAX_DEFENDANT_NAME_LENGTH}
                autoComplete="new-password"
                className={`w-full px-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.defendantFirstName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
              />
              {errors.defendantFirstName ? (
                <p className="text-xs text-red-500 mt-1">{errors.defendantFirstName}</p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                  {formData.defendantFirstName.length}/{VALIDATION_RULES.MAX_DEFENDANT_NAME_LENGTH}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Defendant Last Name *
              </label>
              <input
                type="text"
                value={formData.defendantLastName}
                onChange={(e) => handleFieldChange('defendantLastName', lettersOnly(e.target.value).toUpperCase())}
                onBlur={() => handleFieldBlur('defendantLastName')}
                disabled={isVoid}
                maxLength={VALIDATION_RULES.MAX_DEFENDANT_NAME_LENGTH}
                autoComplete="new-password"
                className={`w-full px-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.defendantLastName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
              />
              {errors.defendantLastName ? (
                <p className="text-xs text-red-500 mt-1">{errors.defendantLastName}</p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                  {formData.defendantLastName.length}/{VALIDATION_RULES.MAX_DEFENDANT_NAME_LENGTH}
                </p>
              )}
            </div>
          </div>

          {/* SSN and DOB */}
          <div className="contents">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                SSN
              </label>
              <input
                type="text"
                value={formData.defendantSsn}
                onChange={(e) => handleFieldChange('defendantSsn', formatSSN(e.target.value))}
                onBlur={() => handleFieldBlur('defendantSsn')}
                disabled={isVoid}
                placeholder="XXX-XX-XXXX"
                maxLength={VALIDATION_RULES.MAX_SSN_LENGTH}
                className={`w-full px-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.defendantSsn ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
              />
              {errors.defendantSsn ? (
                <p className="text-xs text-red-500 mt-1">{errors.defendantSsn}</p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Format: XXX-XX-XXXX</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date of Birth
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  value={dobText}
                  onChange={(e) => handleDateTextChange('defendantDob', e.target.value)}
                  onBlur={() => handleDateTextBlur('defendantDob')}
                  disabled={isVoid}
                  placeholder="MM/DD/YYYY"
                  maxLength={10}
                  autoComplete="off"
                  className={`w-full px-4 py-2 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.defendantDob ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => dobDatePickerRef.current?.showPicker()}
                  disabled={isVoid}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <CalendarIcon className="h-5 w-5" />
                </button>
                <input
                  type="date"
                  ref={dobDatePickerRef}
                  tabIndex={-1}
                  aria-hidden="true"
                  value={formatDateForInput(formData.defendantDob) || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const dateValue = new Date(e.target.value + 'T00:00:00');
                      handleFieldChange('defendantDob', dateValue);
                      setDobText(formatDateForDisplay(dateValue));
                    }
                  }}
                  className="sr-only"
                />
              </div>
              {errors.defendantDob ? (
                <p className="text-xs text-red-500 mt-1">{errors.defendantDob}</p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">MM/DD/YYYY (2-digit year OK)</p>
              )}
            </div>
          </div>

          {/* Case Number and Court City */}
          <div className="contents">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Case Number
              </label>
              <input
                type="text"
                value={formData.caseNumber}
                onChange={(e) => handleFieldChange('caseNumber', e.target.value.toUpperCase())}
                disabled={isVoid}
                maxLength={VALIDATION_RULES.MAX_CASE_NUMBER_LENGTH}
                className={`w-full px-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300 dark:border-gray-600 ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                {formData.caseNumber.length}/{VALIDATION_RULES.MAX_CASE_NUMBER_LENGTH}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Court City *
              </label>
              <div className="relative" ref={cityDropdownRef}>
                <input
                  type="text"
                  value={citySearch}
                  onChange={(e) => {
                    setCitySearch(lettersOnly(e.target.value));
                    setShowCityDropdown(true);
                    setHighlightedCityIndex(0);
                  }}
                  onFocus={() => {
                    // Clear search to show all options when dropdown opens
                    // Only clear if it matches the currently selected city (pre-populated from edit mode)
                    if (formData.city) {
                      const currentCityText = `${toTitleCase(formData.city.name)}, ${formData.city.stateCode}`;
                      if (citySearch === currentCityText) {
                        setCitySearch('');
                      }
                    }
                    setShowCityDropdown(true);
                    setHighlightedCityIndex(0);
                  }}
                  onBlur={(e) => {
                    // If focus is moving within the dropdown, don't restore text yet
                    if (cityDropdownRef.current?.contains(e.relatedTarget)) {
                      return;
                    }
                    // Restore the search text to the currently selected city
                    if (formData.city) {
                      setCitySearch(`${toTitleCase(formData.city.name)}, ${formData.city.stateCode}`);
                    } else {
                      setCitySearch('');
                    }
                  }}
                  onKeyDown={handleCityKeyDown}
                  disabled={isVoid}
                  placeholder="Search city..."
                  autoComplete="new-password"
                  className={`w-full px-4 py-2 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.city ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                />
                {loadingCities ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <OathSpinner size="sm" />
                  </div>
                ) : (
                  <ChevronDownIcon className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none ${isVoid ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`} />
                )}
                {showCityDropdown && filteredCities.length > 0 && (
                  <div
                    ref={cityListRef}
                    className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                  >
                    {filteredCities.map((c, index) => (
                      <button
                        key={`${c.name}-${c.stateCode}`}
                        type="button"
                        onClick={() => handleCitySelected(c)}
                        className={`w-full px-4 py-2 text-left text-gray-900 dark:text-white ${
                          index === highlightedCityIndex
                            ? 'bg-primary/10 dark:bg-primary/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {toTitleCase(c.name)}, {c.stateCode}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {errors.city && (
                <p className="text-xs text-red-500 mt-1">{errors.city}</p>
              )}
            </div>
          </div>

          {/* Rate section — always lives in its own amber card once multi-rate is
              available. Keeps the Rate selector + any required proof-attribute
              fields visually grouped regardless of which rate is selected. */}
          {!isVoid && formData.city && filteredRates.length > 1 && (
            <div className="md:col-span-4 border rounded-xl p-3 bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30">
              {/* Rate on its own compact row so it doesn't stretch the full card width */}
              <div className="sm:max-w-sm">
                {rateSelectorMarkup}
              </div>

              {/* Required documentation block — clean uniform grid so attr labels/inputs all line up */}
              {isVariableRate && selectedRate?.proofAttributeTypes?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-primary/20 dark:border-primary/30">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Required documentation for this rate
                  </p>
                  {/* Auto-fit packs as many attrs as fit given a 200px min column — 5 attrs
                      slot onto one row at modal width, and fewer attrs still fill the row. */}
                  <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
                    {selectedRate.proofAttributeTypes.map(attr => (
                      <div key={attr.id}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {attr.name} {attr.required && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="text"
                          value={proofAttributeValues.find(v => v.proofAttributeTypeId === attr.id)?.value || ''}
                          onChange={(e) => handleProofAttributeChange(attr.id, e.target.value)}
                          className={`w-full px-4 py-2 border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors[`proofAttribute_${attr.id}`] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                          placeholder={attr.name}
                        />
                        {errors[`proofAttribute_${attr.id}`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`proofAttribute_${attr.id}`]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rate description — subtle full-width footnote inside the card */}
              {rateDescriptionText && (
                <p className="mt-3 pt-3 border-t border-primary/20 dark:border-primary/30 text-xs italic text-gray-600 dark:text-gray-400 leading-snug">
                  {rateDescriptionText}
                </p>
              )}
            </div>
          )}

          {/* SC Actual Premium - Only shows when SC city is selected */}
          {isSouthCarolina && formData.executingState?.toUpperCase() === 'SC' && (
            <div className="contents">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Actual SC Premium *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={scAmountDisplay}
                    onChange={(e) => handleCurrencyChange('scAmount', e.target.value, setScAmountDisplay)}
                    onBlur={() => handleFieldBlur('scAmount')}
                    disabled={isVoid}
                    placeholder="0.00"
                    className={`w-full pl-8 pr-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.scAmount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                  />
                </div>
                {errors.scAmount ? (
                  <p className="text-xs text-red-500 mt-1">{errors.scAmount}</p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Required for powers executed in South Carolina</p>
                )}
              </div>
            </div>
          )}

          {/* Offense — sits side-by-side with Executing Agent on wider viewports */}
          <div className="md:col-span-2 self-start">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Offense
            </label>
            <textarea
              value={formData.offense}
              onChange={(e) => handleFieldChange('offense', e.target.value.toUpperCase())}
              disabled={isVoid}
              rows={2}
              maxLength={VALIDATION_RULES.MAX_OFFENSE_LENGTH}
              className={`w-full px-4 py-2 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300 dark:border-gray-600 ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
              {formData.offense.length}/{VALIDATION_RULES.MAX_OFFENSE_LENGTH}
            </p>
          </div>

          {/* Executing Agent — paired next to Offense on md+ */}
          <div className="contents">
            <div className="md:col-span-2 self-start">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Executing Agent *
              </label>
              <div className="relative" ref={agentDropdownRef}>
                <input
                  type="text"
                  value={agentSearch}
                  onChange={(e) => {
                    setAgentSearch(lettersOnly(e.target.value));
                    setShowAgentDropdown(true);
                    setHighlightedAgentIndex(0);
                  }}
                  onFocus={() => {
                    // Clear search to show all options when dropdown opens
                    // Only clear if it matches the currently selected agent (pre-populated from edit mode)
                    if (formData.executingAgent) {
                      const currentAgentText = `${formData.executingAgent.firstName} ${formData.executingAgent.lastName}`;
                      if (agentSearch === currentAgentText) {
                        setAgentSearch('');
                      }
                    }
                    setShowAgentDropdown(true);
                    setHighlightedAgentIndex(0);
                  }}
                  onBlur={(e) => {
                    // If focus is moving within the dropdown, don't restore text yet
                    if (agentDropdownRef.current?.contains(e.relatedTarget)) {
                      return;
                    }
                    // Restore the search text to the currently selected agent
                    if (formData.executingAgent) {
                      setAgentSearch(`${formData.executingAgent.firstName} ${formData.executingAgent.lastName}`);
                    } else {
                      setAgentSearch('');
                    }
                  }}
                  onKeyDown={handleAgentKeyDown}
                  disabled={isVoid}
                  placeholder="Search agent..."
                  autoComplete="new-password"
                  className={`w-full px-4 py-2 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.executingAgent ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} ${isVoid ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                />
                {loadingAgents ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <OathSpinner size="sm" />
                  </div>
                ) : (
                  <ChevronDownIcon className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none ${isVoid ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`} />
                )}
                {showAgentDropdown && filteredAgents.length > 0 && (
                  <div
                    ref={agentListRef}
                    className="absolute z-20 w-full bottom-full mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                  >
                    {filteredAgents.map((a, index) => (
                      <button
                        key={a.executingAgentId}
                        type="button"
                        onClick={() => handleAgentSelected(a)}
                        className={`w-full px-4 py-2 text-left text-gray-900 dark:text-white flex items-center justify-between gap-2 ${
                          index === highlightedAgentIndex
                            ? 'bg-primary/10 dark:bg-primary/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="truncate">{a.firstName} {a.lastName}</span>
                        {isPrimaryAgent(a) && (
                          <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-primary/15 text-primary dark:bg-primary/25 dark:text-primary-light">
                            Primary
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {errors.executingAgent && (
                <p className="text-xs text-red-500 mt-1">{errors.executingAgent}</p>
              )}
            </div>
          </div>

          {/* Void Power Alert */}
          {isVoid && (
            <div className="md:col-span-4">
              <OathAlert variant="warning">
                This is a void power. Most fields are disabled. Only power number is required.
              </OathAlert>
            </div>
          )}
        </div>
      </OathModal>

      {/* Threshold Confirmation Dialog */}
      <OathModal
        isOpen={showThresholdDialog}
        onClose={handleThresholdCancel}
        title="Power Amount Exceeds Denomination"
        size="sm"
        footer={
          <div className="flex gap-3 ml-auto">
            <OathButton variant="outline" onClick={handleThresholdCancel}>
              Cancel
            </OathButton>
            <OathButton onClick={handleThresholdConfirm}>
              Confirm
            </OathButton>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-gray-700 dark:text-gray-300">
                Bond amount <strong>{formatCurrencyWithSymbol(formData.powerAmount)}</strong> is greater than
                the denomination of the power (<strong>{formatCurrencyWithSymbol(selectedPowerDenomination)}</strong>).
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Are you sure you want to continue?
              </p>
            </div>
          </div>
        </div>
      </OathModal>
    </>
  );
};

export default AddEditPowerModal;
